import type { EmsComponents, NormalizedNullableResponse } from '../../api/ems';
import { normalizePortalCall } from './call';
import type { PortalCall } from './call';
import { normalizeLocation } from './location';
import type { Location } from './location';
import { normalizeNullable } from './normalizeNullable';

export type IncidentWire = EmsComponents['schemas']['PortalIncident'];
export type IncidentAgencyViewWire = IncidentWire['agencyViews'][number];

type NormalizedIncident = NormalizedNullableResponse<IncidentWire>;
type NormalizedIncidentAgencyView = NormalizedNullableResponse<IncidentAgencyViewWire>;
type CallerWire = IncidentWire['caller'];
type NormalizedCaller = NormalizedNullableResponse<CallerWire>;
type LiveVideoWire = CallerWire['liveVideos'][number];

export type LiveVideo = NormalizedNullableResponse<LiveVideoWire>;
export type Caller = Omit<
  NormalizedCaller,
  'liveVideos' | 'currentLocation' | 'currentTrack' | 'lastLocationBySource'
> & {
  liveVideos: LiveVideo[];
  currentLocation: Location | null;
  currentTrack: Location[];
  lastLocationBySource: Location[];
};
export type IncidentAgencyView = Omit<NormalizedIncidentAgencyView, 'calls'> & {
  calls: PortalCall[];
};
export type Incident = Omit<NormalizedIncident, 'caller' | 'agencyViews'> & {
  caller: Caller;
  agencyViews: IncidentAgencyView[];
};

export type Attachment = Caller['attachments'][number];
export type InvolvedAgency = Incident['involvedAgencies'][number];
export type CollaborationStateRecord = InvolvedAgency['stateHistory'][number];

export type DeviceInfo = NonNullable<Caller['deviceInfo']>;
export type MobileAppDeviceInfo = NonNullable<Caller['dataSources']['currentMobileApp']>['deviceInfo'];
export type EmergencyContact = Caller['contacts'][number];
export type ReportedProblems = NonNullable<Caller['reportedProblems']>;
export type PermanentResidence = NonNullable<Caller['permanentResidence']>;
export type MedicData = NonNullable<Caller['medicData']>;
export type UserInfo = LiveVideo['startedBy'];
export type GeoPoint = NonNullable<InvolvedAgency['position']>;

export type AgencyRole = IncidentAgencyView['role'];
export type CollaborationState = CollaborationStateRecord['state'];
export type LiveVideoState = Incident['liveVideoState'];
export type LiveVideoSource = LiveVideo['source'];
export type DeviceOs = DeviceInfo['system'];
export type InternetConnectivity = DeviceInfo['internetConnectivity'];
export type InternetConnectionType = MobileAppDeviceInfo['internetConnectionType'];
export type Sex = MedicData['person']['sex'];
export type BloodType = MedicData['person']['bloodType'];

export type LiveVideoAppleEed = Extract<LiveVideo, { source: 'AppleEed' }>;
export type LiveVideoWebApp = Extract<LiveVideo, { source: 'WebApp' }>;
export type LiveVideoGoogleEls = Extract<LiveVideo, { source: 'GoogleEls' }>;

export function normalizeIncidentAgencyView(view: IncidentAgencyViewWire): IncidentAgencyView {
  return {
    ...view,
    calls: view.calls.map(normalizePortalCall),
  };
}

function normalizeLiveVideo(video: LiveVideoWire): LiveVideo {
  return {
    ...video,
    endedAtUtc: video.endedAtUtc ?? null,
  };
}

function normalizeMedicData(medicData: CallerWire['medicData']): Caller['medicData'] {
  if (medicData == null) {
    return null;
  }

  return {
    ...medicData,
    person: {
      ...medicData.person,
      yearOfBirth: medicData.person.yearOfBirth ?? null,
      height: medicData.person.height ?? null,
      weight: medicData.person.weight ?? null,
    },
    conditions: {
      ...medicData.conditions,
      pregnancy: {
        ...medicData.conditions.pregnancy,
        description: medicData.conditions.pregnancy.description ?? null,
      },
      description: medicData.conditions.description ?? null,
    },
    assistance: {
      ...medicData.assistance,
      description: medicData.assistance.description ?? null,
    },
    organDonation:
      medicData.organDonation == null
        ? null
        : {
            ...medicData.organDonation,
            description: medicData.organDonation.description ?? null,
          },
    medications: medicData.medications ?? null,
    allergies: medicData.allergies ?? null,
    advanceDirectives: medicData.advanceDirectives ?? null,
    notes: {
      caller: medicData.notes.caller ?? null,
      nextOfKin: medicData.notes.nextOfKin ?? null,
    },
  };
}

function normalizeDeviceInfo(deviceInfo: CallerWire['deviceInfo']): Caller['deviceInfo'] {
  if (deviceInfo == null) {
    return null;
  }

  return {
    ...deviceInfo,
    batteryLevel: deviceInfo.batteryLevel ?? null,
    systemVersion: deviceInfo.systemVersion ?? null,
  };
}

function normalizePermanentResidence(
  residence: CallerWire['permanentResidence'],
): Caller['permanentResidence'] {
  if (residence == null) {
    return null;
  }

  return {
    ...residence,
    hint: residence.hint ?? null,
    coordinates: residence.coordinates ?? null,
  };
}

function normalizeDataSources(dataSources: CallerWire['dataSources']): Caller['dataSources'] {
  return {
    currentMobileApp: normalizeNullable(dataSources.currentMobileApp ?? null),
    currentMobileAppSms: normalizeNullable(dataSources.currentMobileAppSms ?? null),
    currentAppleEed: normalizeNullable(dataSources.currentAppleEed ?? null),
    currentGoogleEls: normalizeNullable(dataSources.currentGoogleEls ?? null),
    webApp: dataSources.webApp.map(normalizeNullable),
    aml: dataSources.aml.map(normalizeNullable),
  };
}

function normalizeCaller(caller: CallerWire): Caller {
  return {
    ...caller,
    phoneNumber: caller.phoneNumber ?? null,
    chatId: caller.chatId ?? null,
    fullName: caller.fullName ?? null,
    email: caller.email ?? null,
    insuranceNumber: caller.insuranceNumber ?? null,
    medicData: normalizeMedicData(caller.medicData),
    permanentResidence: normalizePermanentResidence(caller.permanentResidence),
    contacts: caller.contacts.map((contact) => ({
      ...contact,
      email: contact.email ?? null,
      relationship: contact.relationship ?? null,
    })),
    reportedProblems: caller.reportedProblems ?? null,
    preferredLocationSource: caller.preferredLocationSource ?? null,
    currentLocation:
      caller.currentLocation == null ? null : normalizeLocation(caller.currentLocation),
    currentTrack: caller.currentTrack.map(normalizeLocation),
    lastLocationBySource: caller.lastLocationBySource.map(normalizeLocation),
    deviceInfo: normalizeDeviceInfo(caller.deviceInfo),
    liveVideos: caller.liveVideos.map(normalizeLiveVideo),
    attachments: caller.attachments.map(normalizeNullable),
    dataSources: normalizeDataSources(caller.dataSources),
  };
}

export function normalizeIncident(incident: IncidentWire): Incident {
  return {
    ...incident,
    label: incident.label ?? null,
    summary: incident.summary ?? null,
    callSign: incident.callSign ?? null,
    ateMessage: incident.ateMessage ?? null,
    closedAtUtc: incident.closedAtUtc ?? null,
    caller: normalizeCaller(incident.caller),
    patient: {
      ...incident.patient,
      description: incident.patient.description ?? null,
    },
    involvedAgencies: incident.involvedAgencies.map((agency) => ({
      ...agency,
      position: agency.position ?? null,
      invitedBy: agency.invitedBy ?? null,
    })),
    agencyViews: incident.agencyViews.map(normalizeIncidentAgencyView),
    activeLiveVideoId: incident.activeLiveVideoId ?? null,
    appleEedCapabilities: incident.appleEedCapabilities ?? null,
    appleEedAdditionalDataExpiresAtUtc: incident.appleEedAdditionalDataExpiresAtUtc ?? null,
    chatMessages:
      incident.chatMessages == null
        ? null
        : incident.chatMessages.map((message) => ({
            ...message,
            message: message.message ?? null,
            attachedImageUrl: message.attachedImageUrl ?? null,
          })),
    liveState: {
      ...incident.liveState,
      callerMessagePreview: incident.liveState.callerMessagePreview ?? null,
    },
  };
}

type ExactlyTheUnion<TUnion extends string> = { [TMember in TUnion]: TMember };

export const AgencyRole = {
  Collaborating: 'Collaborating',
  Supervising: 'Supervising',
} as const satisfies ExactlyTheUnion<AgencyRole>;

export const CollaborationState = {
  Activated: 'Activated',
  Invited: 'Invited',
  Accepted: 'Accepted',
  Declined: 'Declined',
  Supervising: 'Supervising',
} as const satisfies ExactlyTheUnion<CollaborationState>;

export const LiveVideoState = {
  Inactive: 'Inactive',
  Active: 'Active',
  Ended: 'Ended',
} as const satisfies ExactlyTheUnion<LiveVideoState>;

export const LiveVideoSource = {
  WebApp: 'WebApp',
  AppleEed: 'AppleEed',
  GoogleEls: 'GoogleEls',
} as const satisfies ExactlyTheUnion<LiveVideoSource>;

export const DeviceOs = {
  Unknown: 'Unknown',
  iOS: 'iOS',
  Android: 'Android',
  WindowsPhone: 'WindowsPhone',
  Aligator: 'Aligator',
} as const satisfies ExactlyTheUnion<DeviceOs>;

export const InternetConnectivity = {
  Unknown: 'Unknown',
  Online: 'Online',
  Offline: 'Offline',
} as const satisfies ExactlyTheUnion<InternetConnectivity>;

export const InternetConnectionType = {
  Unknown: 'Unknown',
  NoConnection: 'NoConnection',
  WiFi: 'WiFi',
  Cellular2G: 'Cellular2G',
  Cellular3G: 'Cellular3G',
  Cellular4G: 'Cellular4G',
  Cellular5G: 'Cellular5G',
} as const satisfies ExactlyTheUnion<InternetConnectionType>;

const internetConnectionLabels = {
  Unknown: 'Unknown',
  NoConnection: 'NoConnection',
  WiFi: 'WiFi',
  Cellular2G: 'G2',
  Cellular3G: 'G3',
  Cellular4G: 'G4',
  Cellular5G: 'G5',
} as const satisfies Record<InternetConnectionType, string>;

export function internetConnectionLabel(connectionType: InternetConnectionType): string {
  return internetConnectionLabels[connectionType];
}

export const Sex = {
  Unknown: 'Unknown',
  Male: 'Male',
  Female: 'Female',
  Other: 'Other',
  Intersex: 'Intersex',
} as const satisfies ExactlyTheUnion<Sex>;

export const BloodType = {
  Unknown: 'Unknown',
  ZeroNegative: 'ZeroNegative',
  ZeroPositive: 'ZeroPositive',
  ANegative: 'ANegative',
  APositive: 'APositive',
  BNegative: 'BNegative',
  BPositive: 'BPositive',
  ABNegative: 'ABNegative',
  ABPositive: 'ABPositive',
  ZeroUnknown: 'ZeroUnknown',
  AUnknown: 'AUnknown',
  BUnknown: 'BUnknown',
  ABUnknown: 'ABUnknown',
  HH: 'HH',
} as const satisfies ExactlyTheUnion<BloodType>;
